import { cva, type VariantProps } from "class-variance-authority";
import type * as React from "react";

import { cn } from "@/lib/utils";

const labelVariants = cva(
  "inline-flex items-center gap-0.5 font-medium select-none group-data-[disabled=true]:pointer-events-none group-data-[disabled=true]:opacity-50 peer-disabled:cursor-not-allowed peer-disabled:opacity-50",
  {
    variants: {
      size: {
        sm: "text-sm",
        default: "text-base",
      },
    },
    defaultVariants: {
      size: "sm",
    },
  },
);

type LabelProps = React.ComponentProps<"label"> &
  VariantProps<typeof labelVariants> & {
    /**
     * Renders a trailing indicator after the label text.
     * - `required`: destructive `*` (visual; pair with `aria-required` on the control).
     * - `optional`: muted-foreground `(optional)` in regular weight.
     */
    variant?: "default" | "required" | "optional";
  };

function Label({
  className,
  children,
  size,
  variant = "default",
  ...props
}: LabelProps) {
  return (
    <label
      data-slot="label"
      data-variant={variant}
      className={cn(labelVariants({ size }), className)}
      {...props}
    >
      {children}
      {variant === "required" && (
        <span aria-hidden className="text-destructive">
          *
        </span>
      )}
      {variant === "optional" && (
        <span className="font-normal text-muted-foreground">(optional)</span>
      )}
    </label>
  );
}

export { Label };
